import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Html5QrcodeScanner, Html5QrcodeScannerState } from 'html5-qrcode';
import apiClient from '@shared/config/axiosConfig';
import './QRScanner.css';

const QRScanner = ({ isOpen, onClose, onScanSuccess, maquinaInfo }) => {
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);
  const html5QrcodeScannerRef = useRef(null);

  const onScanSuccessHandler = useCallback(async (decodedText) => {
    // console.log('QR escaneado:', decodedText);
    
    try {
      // Limpiar el scanner
      if (html5QrcodeScannerRef.current) {
        await html5QrcodeScannerRef.current.clear();
        html5QrcodeScannerRef.current = null;
      }

      // Parsear y validar el contenido del QR
      let qrData;
      try {
        qrData = typeof decodedText === 'string' ? JSON.parse(decodedText) : decodedText;
        // console.log('QR parseado correctamente:', qrData);
      } catch (parseError) {
        console.error('Error parseando QR JSON:', parseError);
        // Si no es JSON válido, intentar usar como texto plano
        qrData = { raw: decodedText };
      }

      // Validar el QR con el backend usando axiosConfig
      const response = await apiClient.post('/maquinas/validar-qr', {
        qrData: qrData,
        rawQrData: decodedText,
        idmaquinaEsperada: maquinaInfo?.idmaquina
      });

      const validationResult = response.data;

      if (validationResult.valido) {
        setScanResult({
          success: true,
          maquina: validationResult.maquina,
          timestamp: validationResult.timestamp
        });
        
        // Notificar al componente padre
        onScanSuccess(validationResult);
      } else {
        setScanResult({
          success: false,
          error: validationResult.error
        });
        setError(validationResult.error);
      }

    } catch (error) {
      console.error('Error validando QR:', error);
      
      let errorMessage = 'Error validando el código QR';
      
      if (error.response) {
        // El servidor respondió con un error
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          errorMessage = 'No autorizado. Por favor, inicia sesión nuevamente.';
        } else if (status === 403) {
          errorMessage = 'No tienes permisos para validar códigos QR.';
        } else if (status === 404) {
          errorMessage = 'Servicio de validación no disponible.';
        } else if (status >= 500) {
          errorMessage = 'Error del servidor. Intenta nuevamente.';
        } else if (data && data.message) {
          errorMessage = data.message;
        } else if (data && data.error) {
          errorMessage = data.error;
        }
      } else if (error.request) {
        // La petición fue hecha pero no se recibió respuesta
        errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión.';
      } else {
        // Error en la configuración de la petición
        errorMessage = error.message || 'Error inesperado al validar QR';
      }
      
      setError(errorMessage);
      setScanResult({
        success: false,
        error: errorMessage
      });
    }
  }, [maquinaInfo, onScanSuccess]);

  const onScanErrorHandler = useCallback((error) => {
    // Solo mostrar errores relevantes (no los de "QR code not found")
    const errorMessage = error?.toString() || error;
    
    // Filtrar errores comunes que no son problemáticos
    const ignoredErrors = [
      'QR code not found',
      'No QR code found',
      'NotFoundException',
      'No MultiFormat Readers were able to detect the code'
    ];
    
    const shouldIgnore = ignoredErrors.some(ignoredError => 
      errorMessage.includes(ignoredError)
    );
    
    if (!shouldIgnore) {
      console.warn('QR Scan error:', errorMessage);
      // Solo mostrar al usuario errores críticos
      if (errorMessage.includes('Camera') || errorMessage.includes('Permission')) {
        setError(`Error de cámara: ${errorMessage}`);
      }
    }
  }, []);

  const cleanupScanner = useCallback(async () => {
    try {
      if (html5QrcodeScannerRef.current) {
        const scannerState = html5QrcodeScannerRef.current.getState();
        if (scannerState === Html5QrcodeScannerState.SCANNING) {
          await html5QrcodeScannerRef.current.clear();
        }
        html5QrcodeScannerRef.current = null;
      }
    } catch (error) {
      console.error('Error limpiando scanner:', error);
    }
  }, []);

  const initializeScanner = useCallback(() => {
    try {
      // console.log('=== INICIANDO SCANNER QR ===');
      
      // Verificar que el elemento existe antes de inicializar
      const element = document.getElementById('qr-reader');
      // console.log('Elemento qr-reader encontrado:', !!element, element);
      
      if (!element) {
        console.error('Elemento qr-reader no encontrado en el DOM');
        setError('Error: No se pudo inicializar el scanner QR');
        return;
      }

      // Verificar si ya hay un scanner activo
      if (html5QrcodeScannerRef.current) {
        // console.log('Scanner ya existe, limpiando antes de crear uno nuevo');
        cleanupScanner();
      }

      // console.log('Inicializando nuevo scanner...');

      // Configuración optimizada para tablets Samsung Active 5 Pro
      const config = {
        fps: 10,
        qrbox: {
          width: 250,
          height: 250
        },
        aspectRatio: 1.0,
        disableFlip: false,
        videoConstraints: {
          facingMode: "environment", // Cámara trasera
          advanced: [
            {
              focusMode: "continuous",
              exposureMode: "continuous",
              whiteBalanceMode: "continuous"
            }
          ]
        }
      };

      html5QrcodeScannerRef.current = new Html5QrcodeScanner(
        "qr-reader",
        config,
        false // verbose logging
      );

      // console.log('Scanner creado, renderizando...');
      
      html5QrcodeScannerRef.current.render(
        onScanSuccessHandler,
        onScanErrorHandler
      );

      // console.log('Scanner inicializado y renderizado correctamente');
      setError(null);
    } catch (err) {
      console.error('Error inicializando scanner:', err);
      setError(`Error al inicializar el escáner de QR: ${err.message}`);
    }
  }, [onScanSuccessHandler, onScanErrorHandler, cleanupScanner]);

  useEffect(() => {
    // console.log('useEffect QRScanner:', { isOpen, hasScanner: !!html5QrcodeScannerRef.current });
    
    if (isOpen) {
      // Limpiar estados previos cuando se abre el modal
      setScanResult(null);
      setError(null);
      
      if (!html5QrcodeScannerRef.current) {
        // Usar un pequeño delay para asegurar que el DOM esté renderizado
        const timer = setTimeout(() => {
          const element = document.getElementById('qr-reader');
          // console.log('Buscando elemento qr-reader:', element);
          
          if (element) {
            // console.log('Elemento encontrado, inicializando scanner...');
            initializeScanner();
          } else {
            console.error('Elemento qr-reader no encontrado en el DOM');
            setError('Error: No se pudo inicializar el escáner');
          }
        }, 100);

        return () => clearTimeout(timer);
      }
    }
    
    // Limpiar cuando se cierra
    if (!isOpen && html5QrcodeScannerRef.current) {
      cleanupScanner();
    }

    return () => {
      if (html5QrcodeScannerRef.current) {
        cleanupScanner();
      }
    };
  }, [isOpen, initializeScanner, cleanupScanner]);

  const handleClose = async () => {
    await cleanupScanner();
    setScanResult(null);
    setError(null);
    onClose();
  };

  const handleRetry = async () => {
    // console.log('=== REINICIANDO SCANNER ===');
    setScanResult(null);
    setError(null);
    
    // Asegurar que el scanner previo esté limpio
    await cleanupScanner();
    
    // Esperar un momento para que el DOM se actualice
    setTimeout(() => {
      initializeScanner();
    }, 200);
  };

  const handleMarkAsInaccessible = () => {
    // console.log('Máquina marcada como inaccesible:', maquinaInfo);
    
    // Crear un resultado simulado para máquina inaccesible
    const inaccessibleResult = {
      valido: true,
      esInaccesible: true,
      maquina: maquinaInfo,
      timestamp: new Date().toISOString()
    };
    
    // Notificar al componente padre que la máquina es inaccesible
    onScanSuccess(inaccessibleResult);
    
    // Cerrar el modal
    handleClose();
  };

  // Función de debug para mostrar información del estado
  const debugScanner = () => {
    // console.log('=== DEBUG SCANNER ===');
    // console.log('isOpen:', isOpen);
    // console.log('scanner ref:', html5QrcodeScannerRef.current);
    // console.log('element qr-reader:', document.getElementById('qr-reader'));
    // console.log('error:', error);
    // console.log('scanResult:', scanResult);
    // console.log('maquinaInfo:', maquinaInfo);
  };

  if (!isOpen) return null;

  return (
    <div className="qr-scanner-modal">
      <div className="qr-scanner-content">
        <div className="qr-scanner-header">
          <h3>
            <i className="fas fa-qrcode"></i>
            Escanear Código QR
          </h3>
          <button className="qr-close-button" onClick={handleClose} title="Cerrar escáner">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="qr-scanner-body">
          {maquinaInfo && (
            <div className="machine-info">
              <div className="machine-info-grid">
                <div className="machine-info-item">
                  <span className="machine-info-label">#</span>
                  <span className="machine-info-value">{maquinaInfo.numero}</span>
                </div>
                <div className="machine-info-item">
                  <span className="machine-info-label">Tipo</span>
                  <span className="machine-info-value">{maquinaInfo.tipo || 'N/A'}</span>
                </div>
                <div className="machine-info-item">
                  <span className="machine-info-label">Ubicación</span>
                  <span className="machine-info-value">{maquinaInfo.ubicacion}</span>
                </div>
                <div className="machine-info-item">
                  <span className="machine-info-label">Ubicación Específica</span>
                  <span className="machine-info-value">{maquinaInfo.ubicacionEspecifica || 'N/A'}</span>
                </div>
              </div>
              <div className="scan-instruction">
                <i className="fas fa-qrcode" style={{ marginRight: '6px', color: 'var(--accent-color)' }}></i>
                Escanea el código QR para habilitar la inspección
              </div>
            </div>
          )}

          {/* Mostrar el contenedor del scanner cuando está abierto y no hay resultado/error */}
          {!scanResult && !error && (
            <div className="scanner-container">
              <div id="qr-reader"></div>
              <div className="scanner-actions">
                <button 
                  className="btn-inaccessible" 
                  onClick={handleMarkAsInaccessible}
                  title="Marcar como inaccesible si no es posible escanear"
                >
                  <i className="fas fa-ban"></i>
                  Marcar como Inaccesible
                </button>
              </div>
            </div>
          )}

          {scanResult && (
            <div className={`scan-result ${scanResult.success ? 'success' : 'error'}`}>
              {scanResult.success ? (
                <div className="success-result">
                  <i className="fas fa-check-circle"></i>
                  <h4>¡QR Validado Correctamente!</h4>
                  <div style={{ margin: '20px 0', padding: '16px', background: 'var(--background-secondary)', borderRadius: '12px' }}>
                    <p><strong>Máquina:</strong> {scanResult.maquina.numero}</p>
                    <p><strong>Ubicación:</strong> {scanResult.maquina.ubicacion}</p>
                  </div>
                  <p className="success-message">
                    <i className="fas fa-thumbs-up" style={{ marginRight: '8px' }}></i>
                    Ya puedes proceder con la inspección
                  </p>
                  <button className="btn-primary" onClick={handleClose}>
                    <i className="fas fa-arrow-right"></i>
                    Continuar
                  </button>
                </div>
              ) : (
                <div className="error-result">
                  <i className="fas fa-exclamation-triangle"></i>
                  <h4>Error de Validación</h4>
                  <p className="error-message">{scanResult.error}</p>
                  <div className="error-actions">
                    <button className="btn-secondary" onClick={handleRetry}>
                      <i className="fas fa-redo"></i> Reintentar
                    </button>
                    <button className="btn-primary" onClick={handleClose}>
                      <i className="fas fa-times"></i>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && !scanResult && (
            <div className="error-container">
              <i className="fas fa-exclamation-triangle"></i>
              <h4>Error del Escáner</h4>
              <p>{error}</p>
              <div className="error-actions">
                <button className="btn-secondary" onClick={handleRetry}>
                  <i className="fas fa-redo"></i> Reintentar
                </button>
                <button 
                  className="btn-secondary" 
                  onClick={debugScanner} 
                  style={{fontSize: '12px', padding: '5px 10px'}}
                  title="Ver información de debug en consola"
                >
                  <i className="fas fa-bug"></i> Debug
                </button>
                <button 
                  className="btn-inaccessible" 
                  onClick={handleMarkAsInaccessible}
                  title="Marcar como inaccesible si no es posible escanear"
                >
                  <i className="fas fa-ban"></i>
                  Inaccesible
                </button>
                <button className="btn-primary" onClick={handleClose}>
                  <i className="fas fa-times"></i>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;